import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import PrimaryButton from '../../../components/PrimaryButton';
import client from '../../../lib/feathers';

/**
 * /admin/library-imports/:id
 *
 * Review + resolve + execute a CellarTracker import. The import has already
 * been parsed server-side; this page renders the per-row preview grouped by
 * producer match status so the admin can:
 *   - confirm auto-matched producers
 *   - pick listings for unmatched producers (typeahead)
 *   - skip rows that should be ignored
 *   - hit Execute to create the libations / vintages / cellarEntries
 *
 * Bulk actions per producer group so the admin doesn't have to set
 * resolutions one row at a time.
 */

const STATUS_PILLS = {
  ready: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  skip: 'bg-gray-100 text-gray-600',
  executed: 'bg-blue-100 text-blue-800',
  errored: 'bg-red-100 text-red-800',
};

function StatusPill({ status }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${STATUS_PILLS[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
}

function ListingPicker({ value, onChange, onClear }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    try {
      const tokens = q.trim().split(/\s+/).filter(Boolean);
      const res = await client.service('admin/listing').find({
        query: {
          $limit: 10,
          $sort: { name: 1 },
          $and: tokens.map((t) => ({ name: { $iLike: `%${t}%` } })),
          isExcluded: false,
        },
      });
      setResults(res.data || []);
    } catch (e) {
      // ignore — typeahead is best-effort
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
  }, [query, search]);

  if (value) {
    return (
      <span className="inline-flex items-center gap-2 px-2 py-1 bg-green-50 border border-green-200 rounded text-sm">
        <span className="font-medium">{value.listingName}</span>
        <button type="button" onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700">×</button>
      </span>
    );
  }
  return (
    <div className="relative inline-block">
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        placeholder="Search listings…"
        className="border border-gray-300 rounded px-2 py-1 text-sm w-64"
      />
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 mt-1 w-80 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg z-10">
          {results.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => { onChange({ listingId: l.id, listingName: l.name }); setQuery(''); setResults([]); }}
              className="block w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
            >
              <div className="font-medium">{l.name}</div>
              {l.address?.city && (
                <div className="text-xs text-gray-500">{l.address.city}, {l.address.state}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LibraryImportReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [importData, setImportData] = useState(null);
  const [importUser, setImportUser] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await client.service('admin/library-import').get(id);
      setImportData(data);
      // Hydrate the target user so the breadcrumb shows a name instead of
      // a truncated UUID. Best-effort — breadcrumb falls back to id slice.
      try {
        const u = await client.service('admin/user').get(data.userId);
        setImportUser(u);
      } catch (_) { /* ignore */ }
    } catch (e) {
      setError(e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Group rows by producer (CSV's Producer column). Auto-matched + unmatched
  // groups separated so the admin can blow through the easy ones and focus
  // on the unmatched.
  const groupedRows = useMemo(() => {
    if (!importData?.rows) return { matched: {}, unmatched: {} };
    const matched = {};
    const unmatched = {};
    importData.rows.forEach((r) => {
      const bucket = r.producerMatch ? matched : unmatched;
      const key = r.raw.producer || '(no producer)';
      if (!bucket[key]) bucket[key] = [];
      bucket[key].push(r);
    });
    return { matched, unmatched };
  }, [importData]);

  const patchRow = async (rowIndex, resolution) => {
    const updated = await client.service('admin/library-import').patch(id, {
      rowIndex,
      resolution,
    });
    setImportData(updated);
  };

  const applyResolutionToGroup = async (rows, resolution) => {
    // Sequential — keeps the rows jsonb consistent (each patch rewrites it).
    for (const r of rows) {
      // eslint-disable-next-line no-await-in-loop
      await patchRow(r.rowIndex, resolution);
    }
  };

  const execute = async () => {
    if (isExecuting) return;
    if (!window.confirm(`Execute import? ${importData.summary?.ready || 0} rows will be created.`)) return;
    setIsExecuting(true);
    try {
      const updated = await client.service('admin/library-import').patch(id, { action: 'execute' });
      setImportData(updated);
    } catch (e) {
      setError(e.message);
    }
    setIsExecuting(false);
  };

  const deleteImport = async () => {
    if (!window.confirm('Delete this import? The libraryImport row goes away; any cellar entries already created stay put.')) return;
    try {
      await client.service('admin/library-import').remove(id);
      navigate(`/admin/users/${importData.userId}/library-import`);
    } catch (e) {
      setError(e.message);
    }
  };

  if (error) {
    return <Page title="Library Import"><div className="p-4 text-red-700">Error: {error}</div></Page>;
  }
  if (!importData) {
    return <Page title="Library Import"><div className="p-4">Loading…</div></Page>;
  }

  const s = importData.summary || {};
  const readyCount = importData.rows.filter((r) => r.status === 'ready').length;
  const pendingCount = importData.rows.filter((r) => r.status === 'pending').length;
  const skipCount = importData.rows.filter((r) => r.status === 'skip').length;
  const executedCount = importData.rows.filter((r) => r.status === 'executed').length;
  const erroredCount = importData.rows.filter((r) => r.status === 'errored').length;

  return (
    <Page title="Library Import">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <Breadcrumb elements={[
          { name: 'Users', href: '/admin/users' },
          {
            name: importUser
              ? `${importUser.firstName || ''} ${importUser.lastName || ''}`.trim() || importUser.username || importData.userId.slice(0, 8)
              : importData.userId.slice(0, 8),
            href: `/admin/users/${importData.userId}`,
          },
          { name: 'Library Import' },
        ]}
        />
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 py-4">
        <div className="bg-white border rounded p-3"><div className="text-xs text-gray-500 uppercase">Total</div><div className="text-2xl font-semibold">{importData.rows.length}</div></div>
        <div className="bg-green-50 border rounded p-3"><div className="text-xs text-gray-500 uppercase">Ready</div><div className="text-2xl font-semibold text-green-700">{readyCount}</div></div>
        <div className="bg-amber-50 border rounded p-3"><div className="text-xs text-gray-500 uppercase">Pending</div><div className="text-2xl font-semibold text-amber-700">{pendingCount}</div></div>
        <div className="bg-gray-50 border rounded p-3"><div className="text-xs text-gray-500 uppercase">Skip</div><div className="text-2xl font-semibold text-gray-700">{skipCount}</div></div>
        <div className="bg-blue-50 border rounded p-3"><div className="text-xs text-gray-500 uppercase">Executed</div><div className="text-2xl font-semibold text-blue-700">{executedCount}</div></div>
        <div className="bg-red-50 border rounded p-3"><div className="text-xs text-gray-500 uppercase">Errored</div><div className="text-2xl font-semibold text-red-700">{erroredCount}</div></div>
      </div>

      <div className="py-2 flex items-center gap-3">
        <div className="text-sm text-gray-500">Status: <strong>{importData.status}</strong></div>
        <div className="ml-auto flex items-center gap-3">
          <button
            type="button"
            onClick={deleteImport}
            className="text-sm text-red-700 hover:text-red-900 px-2 py-1"
          >
            Delete import
          </button>
          <PrimaryButton onClick={execute} disabled={isExecuting || readyCount === 0}>
            {isExecuting ? 'Executing…' : `Execute (${readyCount} ready)`}
          </PrimaryButton>
        </div>
      </div>

      {/* Unmatched producers first — these need admin attention */}
      {Object.keys(groupedRows.unmatched).length > 0 && (
        <section className="py-4">
          <h3 className="text-lg font-medium mb-3">Unmatched producers ({Object.keys(groupedRows.unmatched).length})</h3>
          <p className="text-sm text-gray-500 mb-3">No listing matched the producer name in the CSV. Pick a listing, leave unlinked (libation created with text producer name only), or skip these rows.</p>
          {Object.entries(groupedRows.unmatched).map(([producer, rows]) => (
            <ProducerGroup
              key={`u-${producer}`}
              producer={producer}
              rows={rows}
              matched={false}
              onSetListing={(listing) => applyResolutionToGroup(rows, { listingId: listing.listingId, listingName: listing.listingName })}
              onLeaveUnlinked={() => applyResolutionToGroup(rows, { unlink: true })}
              onSkipAll={() => applyResolutionToGroup(rows, { skip: true })}
              onResetGroup={() => applyResolutionToGroup(rows, null)}
              onPatchRow={patchRow}
            />
          ))}
        </section>
      )}

      {/* Matched producers — confirm or override */}
      <section className="py-4">
        <h3 className="text-lg font-medium mb-3">Matched producers ({Object.keys(groupedRows.matched).length})</h3>
        {Object.entries(groupedRows.matched).map(([producer, rows]) => (
          <ProducerGroup
            key={`m-${producer}`}
            producer={producer}
            rows={rows}
            matched
            onSetListing={(listing) => applyResolutionToGroup(rows, { listingId: listing.listingId })}
            onLeaveUnlinked={() => applyResolutionToGroup(rows, { listingId: null })}
            onSkipAll={() => applyResolutionToGroup(rows, { skip: true })}
            onPatchRow={patchRow}
          />
        ))}
      </section>
    </Page>
  );
}

function ProducerGroup({
  producer, rows, matched, onSetListing, onLeaveUnlinked, onSkipAll, onResetGroup, onPatchRow,
}) {
  const first = rows[0];
  const matchedListing = first.producerMatch;
  const candidates = first.producerCandidates || [];

  // Effective listing per group: if every row shares the same resolution
  // (listingId or unlink), surface that as the group's current state. Lets
  // the admin see at a glance "this group is now Trisaetum (overridden)"
  // instead of seeing the stale auto-match suggestion.
  const allResolved = rows.every((r) => r.resolution);
  const sameListingId = allResolved && rows.every((r) => r.resolution?.listingId === first.resolution?.listingId);
  const sameUnlink = allResolved && rows.every((r) => r.resolution?.unlink === first.resolution?.unlink);
  const sameSkip = allResolved && rows.every((r) => r.resolution?.skip === first.resolution?.skip);
  let effective;
  if (sameSkip && first.resolution?.skip) {
    effective = { kind: 'skip' };
  } else if (sameUnlink && first.resolution?.unlink) {
    effective = { kind: 'unlinked' };
  } else if (sameListingId && first.resolution?.listingId) {
    effective = { kind: 'override', listingName: first.resolution.listingName || '(listing)' };
  } else if (allResolved) {
    effective = { kind: 'mixed' };
  } else {
    effective = null;
  }

  // Default-collapse rows that are now resolved & ready; expand stuff that
  // still needs the admin's eyes.
  const needsReview = !matched || !effective;
  const [expanded, setExpanded] = useState(needsReview);

  return (
    <div className="bg-white border rounded mb-2 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 text-left"
      >
        <span className="text-gray-400">{expanded ? '▼' : '▶'}</span>
        <span className="font-medium">{producer}</span>
        {effective?.kind === 'override' && (
          <span className="text-sm">
            → <span className="font-medium text-green-700">{effective.listingName}</span>
            <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-800">manual</span>
          </span>
        )}
        {effective?.kind === 'unlinked' && (
          <span className="text-sm text-amber-700">→ <em>leave unlinked</em></span>
        )}
        {effective?.kind === 'skip' && (
          <span className="text-sm text-gray-500">→ <em>skipped</em></span>
        )}
        {effective?.kind === 'mixed' && (
          <span className="text-sm text-gray-500">→ <em>mixed resolutions</em></span>
        )}
        {!effective && matched && matchedListing && (
          <span className="text-sm text-gray-500">
            → {matchedListing.listingName}{' '}
            <span className="text-xs">({Math.round(matchedListing.score * 100)}%)</span>
            {matchedListing.score < 0.80 && (
              <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">needs review</span>
            )}
          </span>
        )}
        <span className="ml-auto text-sm text-gray-500">{rows.length} row{rows.length === 1 ? '' : 's'}</span>
      </button>
      {expanded && (
        <div className="border-t bg-gray-50 px-4 py-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-gray-600">Set listing for all {rows.length}:</span>
            <ListingPicker
              value={null}
              onChange={onSetListing}
              onClear={() => {}}
            />
            {candidates.length > 0 && (
              <>
                <span className="text-sm text-gray-500 ml-2">or pick a suggestion:</span>
                {candidates.map((c) => (
                  <button
                    key={c.listingId}
                    type="button"
                    onClick={() => onSetListing(c)}
                    className="text-sm px-2 py-1 bg-white border border-gray-300 rounded hover:bg-gray-100"
                  >
                    {c.listingName} <span className="text-xs text-gray-500">{Math.round(c.score * 100)}%</span>
                  </button>
                ))}
              </>
            )}
            <button type="button" onClick={onLeaveUnlinked} className="text-sm px-2 py-1 text-gray-600 hover:text-gray-900">leave unlinked</button>
            <button type="button" onClick={onSkipAll} className="text-sm px-2 py-1 text-red-600 hover:text-red-800">skip all</button>
            {effective && onResetGroup && (
              <button type="button" onClick={onResetGroup} className="text-sm px-2 py-1 text-gray-500 hover:text-gray-700">reset to auto-match</button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500 text-xs uppercase">
              <tr>
                <th className="py-1 pr-3">Wine</th>
                <th className="py-1 pr-3">Vintage</th>
                <th className="py-1 pr-3">Qty</th>
                <th className="py-1 pr-3">Will create under</th>
                <th className="py-1 pr-3">Libation match</th>
                <th className="py-1 pr-3">Status</th>
                <th className="py-1 pr-3">Row actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                // Per-row "will create under" — explicit resolution overrides the
                // auto-match; explicit unlink shows that too.
                let destination;
                if (r.resolution?.skip) {
                  destination = <span className="text-gray-400 italic">skipped</span>;
                } else if (r.resolution?.unlink) {
                  destination = <span className="text-amber-700 italic">unlinked</span>;
                } else if (r.resolution?.listingId) {
                  destination = <span className="text-green-700 font-medium">{r.resolution.listingName || r.resolution.listingId.slice(0, 8)} <span className="text-xs text-gray-500">(manual)</span></span>;
                } else if (r.producerMatch?.listingId) {
                  destination = <span>{r.producerMatch.listingName} <span className="text-xs text-gray-500">({Math.round(r.producerMatch.score * 100)}%)</span></span>;
                } else {
                  destination = <span className="text-gray-400 italic">no listing</span>;
                }
                return (
                <tr key={r.rowIndex} className="border-t border-gray-200">
                  <td className="py-1 pr-3 align-top">{r.raw.wine}</td>
                  <td className="py-1 pr-3 align-top">{r.raw.vintage === 9999 ? 'NV' : r.raw.vintage}</td>
                  <td className="py-1 pr-3 align-top">{r.raw.quantity}</td>
                  <td className="py-1 pr-3 align-top">{destination}</td>
                  <td className="py-1 pr-3 align-top">
                    {r.libationMatch ? (
                      <span className="text-green-700">{r.libationMatch.libationName} ({Math.round(r.libationMatch.score * 100)}%)</span>
                    ) : (
                      <span className="text-gray-400">will create new</span>
                    )}
                  </td>
                  <td className="py-1 pr-3 align-top"><StatusPill status={r.status} /></td>
                  <td className="py-1 pr-3 align-top">
                    {r.status !== 'executed' && (
                      <button
                        type="button"
                        onClick={() => onPatchRow(r.rowIndex, { skip: !r.resolution?.skip })}
                        className="text-xs text-gray-600 hover:text-gray-900"
                      >
                        {r.resolution?.skip ? 'unskip' : 'skip'}
                      </button>
                    )}
                    {r.error && <div className="text-xs text-red-600 mt-1">{r.error}</div>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
